import { createAsyncThunk } from '@reduxjs/toolkit';

import type { RootState } from '@/store';

import type { ListaAutorizacoesAr } from './dto';
import { comoFalhaDaDecisao, type FalhaAutorizacaoAr } from './erros';
import { autorizacaoArService } from './services';

interface AlvoDaDecisao {
  processoArbitralId: string;
  solicitacaoId: string;
}

export const fetchAutorizacoesAr = createAsyncThunk<
  ListaAutorizacoesAr,
  string,
  { rejectValue: FalhaAutorizacaoAr; state: RootState }
>('autorizacaoAr/fetch', async (processoArbitralId, { rejectWithValue }) => {
  try {
    return await autorizacaoArService.listar(processoArbitralId);
  } catch (error) {
    return rejectWithValue(comoFalhaDaDecisao(error, 'Erro ao carregar os envios de AR'));
  }
});

/**
 * As quatro decisões e a verificação de pagamento seguem o MESMO rito: chamam a API e
 * releem a lista.
 *
 * Reler, em vez de costurar a resposta no estado, é decisão e não preguiça: o processo
 * pode ter mais de um requerido, e um ato mexe em mais de um card — pagar libera a
 * postagem, a ciência digital cancela a carta do vizinho, a varredura reconcilia o que
 * estava parado. Reproduzir aqui qual outro card mudou seria reimplementar no navegador
 * a régua que o servidor acabou de aplicar.
 *
 * No ERRO o thunk apenas rejeita, **com o `code`** — quem decide o que fazer é o hook:
 * `SOLICITACAO_JA_DECIDIDA` traz o estado novo em `details` e dispensa a releitura,
 * `SOLICITACAO_SUPERADA` exige relê-la. Fazer a releitura aqui dentro obrigaria a
 * engolir a segunda falha para não mascarar a primeira.
 */
function decisaoQueRele<Arg extends AlvoDaDecisao>(
  nome: string,
  executar: (arg: Arg) => Promise<unknown>,
) {
  return createAsyncThunk<
    ListaAutorizacoesAr,
    Arg,
    { rejectValue: FalhaAutorizacaoAr; state: RootState }
  >(`autorizacaoAr/${nome}`, async (arg, { rejectWithValue }) => {
    try {
      await executar(arg);
    } catch (error) {
      return rejectWithValue(comoFalhaDaDecisao(error, 'Não foi possível registrar sua decisão'));
    }
    return autorizacaoArService.listar(arg.processoArbitralId);
  });
}

export const autorizarConvenioAr = decisaoQueRele(
  'autorizarConvenio',
  ({ processoArbitralId, solicitacaoId }: AlvoDaDecisao) =>
    autorizacaoArService.autorizarConvenio(processoArbitralId, solicitacaoId),
);

export const devolverEnderecoAr = decisaoQueRele(
  'devolverEndereco',
  ({ processoArbitralId, solicitacaoId, observacao }: AlvoDaDecisao & { observacao: string }) =>
    autorizacaoArService.devolverEndereco(processoArbitralId, solicitacaoId, observacao),
);

export const recusarEnvioAr = decisaoQueRele(
  'recusar',
  ({ processoArbitralId, solicitacaoId, motivo }: AlvoDaDecisao & { motivo: string | null }) =>
    autorizacaoArService.recusar(processoArbitralId, solicitacaoId, motivo),
);

export const desfazerRecusaAr = decisaoQueRele(
  'desfazerRecusa',
  ({ processoArbitralId, solicitacaoId }: AlvoDaDecisao) =>
    autorizacaoArService.desfazerRecusa(processoArbitralId, solicitacaoId),
);

export const verificarPagamentoAr = decisaoQueRele(
  'verificarPagamento',
  ({ processoArbitralId, solicitacaoId }: AlvoDaDecisao) =>
    autorizacaoArService.verificarPagamento(processoArbitralId, solicitacaoId),
);
